import { BaseAPI, PostOptions } from './BaseAPI';
import {
  Campaign,
  campaignActionToString,
  CampaignShopifyProductRules,
  CampaignShopifyRule,
  CampaignStatus,
  Redemption,
  ShopConfiguration,
  ShopifyProduct,
  statusToString,
  Token,
  TokenFilteredByAttributes,
} from './interfaces/campaign';

const NETWORK_TO_STAGE: { [key: number]: string } = {
  1: 'mainnet',
  11155111: 'sepolia',
  10: 'optimism',
  8453: 'base',
};

export class CampaignAPI extends BaseAPI {
  network: string | null;
  baseUrl = 'https://6g5vuj5b15.execute-api.us-east-1.amazonaws.com/prod';

  constructor(network: string | null, jwtToken?: string) {
    super(jwtToken);
    this.network = network;
  }

  pathToUrl(path: string, stage: string | undefined = undefined) {
    if (path.includes('?'))
      return `${this.baseUrl}${path}&stage=${stage || this.network || 'mainnet'}`;
    return `${this.baseUrl}${path}?stage=${stage || this.network || 'mainnet'}`;
  }

  mapPropsToCampaign(campaign: Campaign): Campaign {
    return {
      ...campaign,
      status: getCampaignStatus(campaign),
      statusString: getCampaignStatusString(campaign),
      actionString: campaignActionToString(campaign.action),
      tokenData: {
        ...campaign.tokenData,
        tokenAttributes: campaign.tokenData.tokenAttributes.map((token) => ({
          ...token,
          filterType: 'attributes',
        })),
        tokens: campaign.tokenData.tokens.map((token) => ({
          ...token,
          filterType: 'no_filter',
        })),
        tokenIds: campaign.tokenData.tokenIds.map((token) => ({
          ...token,
          filterType: 'id',
        })),
        tokenRanges: campaign.tokenData.tokenRanges.map((token) => ({
          ...token,
          filterType: 'id_range',
        })),
      },
    };
  }

  fetch(path: string, options: RequestInit = {}, stage: string | undefined = undefined) {
    return super.fetch(this.pathToUrl(path, stage), options);
  }

  put(path: string, options: PostOptions = {}, stage: string | undefined = undefined) {
    return super.put(this.pathToUrl(path, stage), options);
  }

  post(path: string, options: PostOptions = {}, stage: string | undefined = undefined) {
    return super.post(this.pathToUrl(path, stage), options);
  }

  delete(path: string, options: RequestInit = {}, stage: string | undefined = undefined) {
    return super.delete(this.pathToUrl(path, stage), options);
  }

  async fetchShopConfiguration(shop: string): Promise<ShopConfiguration> {
    return this.fetch(`/redeem/shopify_internal/shops/${shop}/configuration`);
  }

  async deleteCampaign(campaignId: number) {
    return this.delete(`/redeem/campaign/${campaignId}`);
  }

  async fetchAllCampaigns(shop: string): Promise<Campaign[]> {
    const campaigns = (await this.fetch(`/redeem/campaign/all?full=true`)) as Campaign[];
    const productIds: number[] = [];
    for (const campaign of campaigns) {
      for (const product of campaign.products) {
        product.title = product.productId.toString();
        productIds.push(product.productId);
      }
    }
    /**
    try {
      const shopifyProducts = await this.refreshShopifyProducts(shop, productIds);
      const shopifyProductNameMap: any = {};
      for (const shopifyProduct of shopifyProducts) {
        shopifyProductNameMap[shopifyProduct.id.toString()] = shopifyProduct.title;
      }
      for (const campaign of campaigns) {
        for (const product of campaign.products) {
          product.title = shopifyProductNameMap[product.productId.toString()] || product.title;
        }
      }
    } catch (e) {
      // Could not process names.  Ignore
    }
    */
    return campaigns.map((campaign) => this.mapPropsToCampaign(campaign));
  }

  async createCampaign(campaign: Partial<Campaign>) {
    return await this.post(`/redeem/campaign/new`, { body: campaign });
  }

  async fetchCampaign(campaignId: string | number): Promise<Campaign> {
    const campaign = await this.fetch(`/redeem/campaign/${campaignId}/internal`);
    return this.mapPropsToCampaign(campaign);
  }

  async addNewTokenToAllowlist(campaignId: number, token: Token) {
    if (token.filterType === 'attributes') {
      return this.addNewTokenFilteredByAttributesToAllowlist(campaignId, token);
    }
    const campaign = await this.post(`/redeem/campaign/${campaignId}/token/new`, {
      body: { ...token, filterType: undefined },
    });
    return this.mapPropsToCampaign(campaign);
  }

  async addNewTokenFilteredByAttributesToAllowlist(
    campaignId: number,
    token: TokenFilteredByAttributes
  ) {
    /**
     * Adding a token filtered by attributes requires multiple API requests.
     */
    const { id } = (await this.post(`/redeem/campaign/${campaignId}/token/attribute/new`, {
      body: { network: token.network, tokenAddress: token.tokenAddress },
    })) as { id: string };

    await Promise.all(
      token.attributeEntries.map(async ({ traitType, value }) => {
        return this.post(`/redeem/campaign/${campaignId}/token/attribute/entry/new`, {
          body: {
            tokenWithAttributesId: id,
            traitType,
            value,
          },
        });
      })
    );
  }

  async updateCampaign({
    campaignId,
    endDate,
    startDate,
    name,
    redemptionsPerToken,
    redemptionsPerAddress,
    snapshotDate,
  }: {
    endDate?: number | null;
    startDate?: number;
    campaignId: string | number;
    name?: string | null;
    redemptionsPerToken?: number | null;
    redemptionsPerAddress?: number | null;
    /** Move the snapshot. Only allowed while it is still in the future. */
    snapshotDate?: number;
  }): Promise<Campaign> {
    const args: {
      endDate?: number | null;
      startDate?: number;
      name?: string | null;
      redemptionsPerToken?: number | null;
      redemptionsPerAddress?: number | null;
      snapshot?: boolean;
      snapshotDate?: number;
    } = {};

    // The server only reads snapshotDate alongside snapshot: true.
    if (snapshotDate !== undefined) {
      args.snapshot = true;
      args.snapshotDate = snapshotDate;
    }

    if (endDate !== undefined) {
      args.endDate = endDate;
    }

    if (startDate !== undefined) {
      args.startDate = startDate;
    }

    if (name !== undefined) {
      args.name = name;
    }

    if (redemptionsPerToken !== undefined) {
      args.redemptionsPerToken = redemptionsPerToken;
    }

    if (redemptionsPerAddress !== undefined) {
      args.redemptionsPerAddress = redemptionsPerAddress;
    }

    const campaign = await this.put(`/redeem/campaign/${campaignId}`, { body: args });

    return this.mapPropsToCampaign(campaign);
  }

  async fetchTokenNFTAttributes(
    network: number,
    tokenAddress: string
  ): Promise<{ traitType: string; value: string }[]> {
    return this.fetch(`/nft/attributes/${tokenAddress}`, {}, NETWORK_TO_STAGE[network]);
  }

  async refreshTokenMetadata(network: number, tokenAddress: string) {
    return this.post(`/nft/refresh/${tokenAddress}`, {}, NETWORK_TO_STAGE[network]);
  }

  async deleteTokenFromAllowlist(campaignId: string | number, token: Token) {
    if (token.filterType === 'no_filter') {
      return this.delete(
        `/redeem/campaign/${campaignId}/token?network=${token.network}&tokenAddress=${token.tokenAddress}`
      );
    }

    if (token.filterType === 'attributes') {
      return this.delete(`/redeem/campaign/${campaignId}/token/attribute/${token.id!}`);
    }

    if (token.filterType === 'id') {
      return this.delete(
        `/redeem/campaign/${campaignId}/token?network=${token.network}&tokenAddress=${token.tokenAddress}&tokenId=${token.tokenId}`
      );
    }

    if (token.filterType === 'id_range') {
      return this.delete(
        `/redeem/campaign/${campaignId}/token?network=${token.network}&tokenAddress=${token.tokenAddress}&minTokenId=${token.minTokenId}&maxTokenId=${token.maxTokenId}`
      );
    }
  }

  async fetchCampaignShopifyProducts(campaignId: number): Promise<ShopifyProduct[]> {
    return this.fetch(`/redeem/campaign/${campaignId}/shopify/products`);
  }

  async addShopifyProduct(campaignId: number, product: Partial<ShopifyProduct>) {
    return this.post(`/redeem/campaign/${campaignId}/shopify/product`, { body: product });
  }

  async deleteShopifyProduct(campaignId: number, productId: number) {
    return this.delete(`/redeem/campaign/${campaignId}/shopify/product/${productId}`);
  }

  async updateShopifyProduct(
    campaignId: number,
    productId: number,
    product: Partial<ShopifyProduct>
  ) {
    return this.put(`/redeem/campaign/${campaignId}/shopify/product/${productId}`, {
      body: product,
    });
  }

  async refreshShopifyProducts(shop: string, productIds: number[]) {
    return this.post(`/redeem/shopify_internal/shops/${shop}/products/update`, {
      body: {
        productIds: productIds,
      },
    });
  }

  async fetchCampaignRedemptions(campaignId: number | string): Promise<Redemption[]> {
    return this.fetch(`/redeem/campaign/${campaignId}/redemption/all`);
  }

  async fetchCampaignRules(campaignId: number | string): Promise<CampaignShopifyProductRules> {
    return this.fetch(`/redeem/campaign/${campaignId}/shopify/rules`);
  }

  async deleteCampaignRule(
    campaignId: number | string,
    productId: number | string,
    ruleId: string
  ) {
    return await this.put(`/redeem/campaign/${campaignId}/shopify/product/${productId}`, {
      body: {
        method: 'delete',
        ruleId,
      },
    });
  }

  async addCampaignRule(
    campaignId: number | string,
    productId: number | string,
    rule: Partial<CampaignShopifyRule>
  ) {
    return await this.put(`/redeem/campaign/${campaignId}/shopify/product/${productId}`, {
      body: {
        method: 'add',
        rule,
      },
    });
  }

  async createLinkAccount(shop: string, cognitoUserId: string): Promise<Object> {
    const res = await this.post(`/redeem/shopify_internal/shops/${shop}/linkAccount`, {
      body: { cognitoUserId: cognitoUserId },
    });
    return res;
  }

  async fetchLinkedAccountsEmails(shop: string): Promise<string[]> {
    return this.fetch(`/redeem/shopify_internal/shops/${shop}/users`);
  }
}

const getCampaignStatusString = (campaign: Campaign) => {
  const now = Date.now();

  // Inactive campaigns (deleted, etc) consider to be completed.
  if (!campaign.active) {
    return statusToString(CampaignStatus.COMPLETED);
  }

  if (campaign.endDate && campaign.endDate < now) {
    return statusToString(CampaignStatus.COMPLETED);
  } else if (campaign.startDate! > now) {
    return statusToString(CampaignStatus.SCHEDULED);
  }

  return statusToString(CampaignStatus.ACTIVE);
};

const getCampaignStatus = (campaign: Campaign) => {
  const now = Date.now();

  if (campaign.endDate && campaign.endDate < now) {
    return CampaignStatus.COMPLETED;
  } else if (campaign.startDate! > now) {
    return CampaignStatus.SCHEDULED;
  }

  return CampaignStatus.ACTIVE;
};
